import React from 'react';
import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';

import MakeAsyncFunction from 'react-redux-promise-listener';
import { getType } from 'typesafe-actions';
import * as welcomeCampaignActions from '../../../../../../redux/welcomeCampaigns/actions';
import { promiseListener } from '../../../../../../redux/store';

import Button from '@material-ui/core/Button';
import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import { SubmitButton } from '../../../../../../components/Form';
import { TextField } from '../../../../../../components/FormikAdapter';
import RewardTemplateSelectV2 from '../../../../../../components/FormikAdapter/RewardTemplateSelect/v2';
import { FormRow } from '../../../../../../components/FormLayout';

//------------------------------------------------------------------------------
// Types
//------------------------------------------------------------------------------

type Props = Pick<DialogProps, 'open' | 'onExit'> & {
  merchantId: string;
  programId: string;
  onClose: () => void;
};

interface NewWelcomeCampaignFormValues {
  validityPeriodDays: number;
  rewardTemplateId: string;
  delayHours: number;
}

//------------------------------------------------------------------------------
// Validation
//------------------------------------------------------------------------------

const ValidationSchema = Yup.object().shape({
  validityPeriodDays: Yup.number().required('Required').positive().integer(),
  rewardTemplateId: Yup.string().required('Required'),
  delayHours: Yup.number()
    .required('Required')
    .integer()
    .min(0, 'Minimum delay is 0'),
});

//------------------------------------------------------------------------------
// Component
//------------------------------------------------------------------------------

export default function NewWelcomeCampaignDialog({
  merchantId,
  programId,
  open,
  onExit,
  onClose,
}: Props) {
  const initialValues: NewWelcomeCampaignFormValues = {
    validityPeriodDays: 60,
    rewardTemplateId: '',
    delayHours: 0,
  };

  return (
    <MakeAsyncFunction
      listener={promiseListener}
      start={getType(welcomeCampaignActions.createWelcomeCampaign)}
      resolve={getType(
        welcomeCampaignActions.api.createWelcomeCampaign.success
      )}
      reject={getType(welcomeCampaignActions.api.createWelcomeCampaign.failure)}
    >
      {(createWelcomeCampaign: any) => (
        <Formik
          initialValues={initialValues}
          validationSchema={ValidationSchema}
          onSubmit={async (values, actions) => {
            try {
              await createWelcomeCampaign({
                ...values,
                loyaltyProgramId: programId,
              });

              actions.setSubmitting(false);
              actions.resetForm();
              onClose();
            } catch {
              actions.setSubmitting(false);
              alert('There was an error submitting this form');
            }
          }}
        >
          {({ isSubmitting, resetForm }) => (
            <Dialog open={open} onClose={onClose} onExit={onExit}>
              <Form translate="no" noValidate>
                <DialogTitle>Create a welcome campaign</DialogTitle>

                <DialogContent>
                  <FormRow>
                    <Field
                      component={RewardTemplateSelectV2}
                      name="rewardTemplateId"
                      label="Reward Template"
                      merchantId={merchantId}
                      autoFocus
                      fullWidth
                      required
                    />
                  </FormRow>

                  <FormRow>
                    <Field
                      component={TextField}
                      name="validityPeriodDays"
                      label="Validity Period (days)"
                      type="number"
                      fullWidth
                      required
                    />
                  </FormRow>

                  <FormRow>
                    <Field
                      component={TextField}
                      name="delayHours"
                      label="Delay delivery by Hour"
                      type="number"
                      fullWidth
                      required
                    />
                  </FormRow>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => {
                      resetForm();
                      onClose();
                    }}
                    color="primary"
                  >
                    Cancel
                  </Button>
                  <SubmitButton
                    type="submit"
                    size="small"
                    isSubmitting={isSubmitting}
                  >
                    Create Campaign
                  </SubmitButton>
                </DialogActions>
              </Form>
            </Dialog>
          )}
        </Formik>
      )}
    </MakeAsyncFunction>
  );
}
